import type { EditJourneyErrors, EditJourneyForm } from '@/model/edit-journey.model'
import type { Driver } from '@/types/driver'
import type { Journey } from '@/types/journey'
import { STATUS_LABEL, STATUS_ORDER } from '@/types/journey-template'
import type { Product } from '@/types/product'
import type { Nullish } from '@logistiek-zonder-papier/lzp-library'

export function isExternalEdit(journeys: Journey[]) {
  return journeys.some(journey => !journey.stroomSignature.document.intern)
}

export function validateEditJourney(
  data: Partial<EditJourneyForm>,
  journeysToEdit: Journey[],
  originalProducts: Product[]
): {
  formErrors: EditJourneyErrors
  productErrors: Record<string, Partial<Record<keyof Product, string>>>
} {
  const formErrors: EditJourneyErrors = {}
  const productErrors: Record<string, Partial<Record<keyof Product, string>>> = {}
  const currentTime = new Date()

  if (data.departureTimestamp != null) {
    if (isNaN(data.departureTimestamp.getTime())) {
      formErrors.departureTimestamp = 'De vertrektijd is in een ongeldig format.'
    } else if (currentTime < data.departureTimestamp) {
      formErrors.departureTimestamp = 'De vertrektijd kan niet in de toekomst liggen.'
    } else {
      const earliestArrival =
        data.arrivalTimestamp ??
        journeysToEdit.reduce((min: Date | Nullish, current) => {
          if (current.transporten[0]?.ontvangstDatum == null) return min

          const time = new Date(current.transporten[0].ontvangstDatum)

          if (min == null) return time

          return min > time ? time : min
        }, data.arrivalTimestamp)

      if (earliestArrival != null && data.departureTimestamp > earliestArrival) {
        formErrors.departureTimestamp = `De vertrektijd moet minimaal voor ${earliestArrival.toLocaleString()} zijn.`
      }
    }
  }

  if (data.arrivalTimestamp != null) {
    if (isNaN(data.arrivalTimestamp.getTime())) {
      formErrors.arrivalTimestamp = 'Deze aankomsttijd is in een ongeldig format.'
    } else if (currentTime < data.arrivalTimestamp) {
      formErrors.arrivalTimestamp = 'De aankomsttijd kan niet in de toekomst liggen.'
    } else {
      const latestDeparture =
        data.departureTimestamp ??
        journeysToEdit.reduce((max: Date | Nullish, current) => {
          if (current.transporten[0]?.vervoerDatum == null) return max

          const time = new Date(current.transporten[0]?.vervoerDatum)

          if (max == null) return time

          return max < time ? time : max
        }, data.departureTimestamp)

      if (latestDeparture != null && data.arrivalTimestamp < latestDeparture) {
        formErrors.arrivalTimestamp = `De aankomsttijd moet minimaal na ${latestDeparture.toLocaleString()} zijn.`
      }
    }
  }

  if (
    data.departureTimestamp != null &&
    data.arrivalTimestamp != null &&
    data.departureTimestamp >= data.arrivalTimestamp
  ) {
    formErrors.arrivalTimestamp = 'De aankomsttijd moet na de vertrektijd vallen.'
    formErrors.departureTimestamp = 'De vertrektijd moet voor de aankomsttijd vallen.'
  }
  if (data.remark == null || data.remark == '' || data.remark?.trim().length === 0) {
    formErrors.remark = 'Opmerking is verplicht.'
  }

  if (!data.userSignedTemplate) {
    formErrors.userSignedTemplate = 'Verklaring is verplicht.'
  }

  if (data.status != null) {
    const lowestStatus = journeysToEdit.reduce((min, current) => {
      const order = STATUS_ORDER[current.status]
      return min > order ? order : min
    }, Number.MAX_SAFE_INTEGER)

    const statusOrder = STATUS_ORDER[data.status.id]
    if (statusOrder < lowestStatus) {
      formErrors.status = 'De status mag niet teruglopend zijn. Kies een verdere status.'
    }

    if (
      statusOrder >= 3 &&
      data.arrivalTimestamp == null &&
      journeysToEdit.some(journey => journey.transporten[0]?.ontvangstDatum == null)
    ) {
      formErrors.arrivalTimestamp = 'Een aankomsttijd is verplicht voor deze status.'
    }

    if (
      journeysToEdit.some(journey => !journey.stroomSignature.document.intern) &&
      data.status.id !== 'FINAAL'
    ) {
      formErrors.status = 'De status voor externe ritten mag niet anders zijn dan finaal.'
    }
  }

  if (data.products && data.products.length > 0) {
    for (let index = 0; index < data.products.length; index++) {
      const product = data.products[index]
      if (product === undefined) continue
      const errors: Partial<Record<keyof Product, string>> = {}
      const productSelected = product.uuid != ''

      const productIsOriginal = originalProducts.some(
        otherProduct => product?.uuid === otherProduct.uuid
      )
      const weight = product.gewicht
      const weightIsDefined = weight != null && String(weight) != ''

      if (weightIsDefined) {
        if (weight <= 0) {
          errors['gewicht'] = 'Gewicht moet groter dan 0 zijn.'
        }

        if (weight > 1000000) {
          errors['gewicht'] = 'Gewicht mag niet groter zijn dan 1000000.'
        }
      }
      if (productSelected && !productIsOriginal) {
        if (!weightIsDefined) {
          errors['gewicht'] = 'Gewicht mag niet leeg zijn.'
        }
        if (product.rdCode == null) {
          errors['rdCode'] = 'RD Code mag niet leeg zijn.'
        }
      }

      if (!productSelected) {
        errors['uuid'] = 'Een product moet worden geselecteerd.'
      }

      if (Object.keys(errors).length > 0) productErrors[index] = errors
    }
  }
  return { formErrors, productErrors }
}

export function generateDisabledFields(
  journeys: Journey[],
  formData: EditJourneyForm
): {
  disabledFields: (keyof EditJourneyForm)[]
  disabledProductFields: (keyof Product | 'remove')[][]
} {
  const disabledFields: (keyof EditJourneyForm)[] = []
  const disabledProductFields: (keyof Product | 'remove')[][] = []

  if (!canEditProducts(journeys, formData)) {
    disabledFields.push('products')
  }

  const isExternal = journeys.some(journey => !journey.stroomSignature.document.intern)
  if (isExternal) {
    disabledFields.push('status')
    disabledFields.push('carrierDriver')
    disabledFields.push('products')
  }
  if (formData.products != null) {
    for (let index = 0; index < formData.products.length; index++) {
      const disabled: (keyof Product | 'remove')[] = []

      disabled.push('remove')

      if (disabled.length > 0) disabledProductFields[index] = disabled
    }
  } else {
    disabledFields.push('products')
  }

  return { disabledFields, disabledProductFields }
}

function getProductsForJourney(journeys: Journey[]) {
  return journeys.map(journey =>
    journey.transporten.flatMap(transport => transport.products.map(product => product.uuid))
  )
}

export function canEditProducts(journeys: Journey[], formData: EditJourneyForm | Nullish) {
  const productsPerJourney = getProductsForJourney(journeys)

  const templates = new Set(
    journeys.map(journey => journey.stroomSignature.document.stroomUUID_UNIQUE)
  ) //THE LEGACY API MAKES ME SAD...

  const allTemplatesEqual = templates.size === 1

  const allJourneysProductsEqual = productsPerJourney.every(values =>
    values.every(value => productsPerJourney[0]?.includes(value))
  )

  return allJourneysProductsEqual && (allTemplatesEqual || formData?.template != null)
}

export function canEditJourneys(journeys: Journey[]) {
  const errors: string[] = []
  let editable = true

  const allJourneysInternalOrFinal = journeys.every(
    journey => journey.stroomSignature.document.intern || journey.status === 'FINAAL'
  )
  if (!allJourneysInternalOrFinal) {
    editable = false
    errors.push('Kan externe ritten die niet finaal zijn niet aanpassen.')
  }
  return {
    editable,
    errors,
  }
}

export function generateTouched(value: boolean): Record<keyof EditJourneyForm, boolean> {
  return {
    products: value,
    carrierDriver: value,
    remark: value,
    arrivalTimestamp: value,
    departureTimestamp: value,
    status: value,
    template: value,
    userSignedTemplate: value,
  }
}

export function initializeEditJourneyForm(journeys: Journey[], drivers: Driver[]): EditJourneyForm {
  return {
    products: canEditProducts(journeys, null) ? getJourneyProducts(journeys) : null,
    carrierDriver: getDriverForEdit(journeys, drivers),
    remark: null,
    arrivalTimestamp: getArrivalTimestampForEdit(journeys),
    departureTimestamp: getDepartureTimestampForEdit(journeys),
    status: getStatusForEdit(journeys),
    template: getTemplateForEdit(journeys),
    userSignedTemplate: null,
  }
}

export function getArrivalTimestampForEdit(journeys: Journey[]) {
  const firstTime = journeys[0]?.transporten[0]?.ontvangstDatum
  const allArrivalTimesEqual = journeys.every(
    journey => journey.transporten[0]?.ontvangstDatum === firstTime
  )

  return allArrivalTimesEqual && firstTime != undefined ? new Date(firstTime) : null
}

export function getDepartureTimestampForEdit(journeys: Journey[]) {
  const firstTime = journeys[0]?.transporten[0]?.vervoerDatum
  const allDepartureTimesEqual = journeys.every(
    journey => journey.transporten[0]?.vervoerDatum === firstTime
  )

  return allDepartureTimesEqual && firstTime != undefined ? new Date(firstTime) : null
}

export function getTemplateForEdit(journeys: Journey[]) {
  const firstTemplate = journeys[0]?.stroomSignature.document
  const allTemplatesEqual = journeys.every(
    journey => journey.stroomSignature.document.stroomUUID === firstTemplate?.stroomUUID
  )

  return allTemplatesEqual ? firstTemplate : null
}

export function getStatusForEdit(journeys: Journey[]) {
  const firstStatus = journeys[0]?.status
  const allStatusesEqual = journeys.every(journey => journey.status === firstStatus)

  return allStatusesEqual && firstStatus != null ? STATUS_LABEL[firstStatus] : null
}

export function getDriverForEdit(journeys: Journey[], drivers: Driver[]) {
  const firstDriver = journeys[0]?.ritVoertuig[0]
  const allDriversEqual = journeys.every(
    journey =>
      journey.ritVoertuig[0]?.voertuigUUID === firstDriver?.voertuigUUID &&
      journey.ritVoertuig[0]?.voertuigOrganisatie.organisatieUUID ===
        firstDriver?.voertuigOrganisatie.organisatieUUID
  )

  return allDriversEqual
    ? drivers.find(
        driver =>
          driver.vehicle?.uuid === firstDriver?.voertuigUUID &&
          driver.vehicle?.organization.organisatieUUID ===
            firstDriver?.voertuigOrganisatie.organisatieUUID
      )
    : null
}

export function getJourneyProducts(journeys: Journey[]) {
  const output: Product[] = []

  const products = journeys
    .flatMap(journey => journey.transporten)
    .flatMap(transport => transport.products)

  for (const product of products) {
    const index = output.findIndex(otherProduct => otherProduct.uuid === product.uuid)

    if (index === -1 || output[index] == null) {
      output.push({ ...product })
      continue
    }

    if (output[index].gewicht !== product.gewicht) {
      output[index].gewicht = null
    }

    if (output[index].rdCode?.id !== product.rdCode?.id) {
      output[index].rdCode = null
    }
  }

  return output
}

export function generateProductOptionalFields(
  formData: EditJourneyForm,
  originalProducts: Product[]
) {
  const output: (keyof Product)[][] = []
  if (formData.products == null) return []

  for (let index = 0; index < formData.products.length; index++) {
    const product = formData.products[index]
    const optional: (keyof Product)[] = []

    const productIsOriginal = originalProducts.some(
      otherProduct => product?.uuid === otherProduct.uuid
    )

    if (product?.uuid == '' || productIsOriginal) {
      optional.push('gewicht')
      optional.push('rdCode')
    }

    output[index] = optional
  }
  return output
}
